/**
 * The summary is plain text in the light format the prompt asks for (see
 * backend/domain/renewal-summary/prompt.ts): a heading line, a paragraph,
 * then "- " bullets, optionally under short heading lines, blank lines
 * between parts. This turns it into blocks the panel renders. It is lenient
 * on purpose: anything that is not a bullet or a recognisable heading is a
 * paragraph, so text in an unexpected shape still reads.
 */

export type SummaryBlock =
  { type: 'heading'; text: string } | { type: 'paragraph'; text: string } | { type: 'list'; items: string[] };

const BULLET = /^\s*(?:[-•*]|\d+[.)])\s+/;
const MAX_HEADING_LENGTH = 100;

/** A single short line with no sentence-ending punctuation reads as a heading when it opens the text or a list. */
const looksLikeHeading = (lines: string[]): boolean =>
  lines.length === 1 && lines[0].length <= MAX_HEADING_LENGTH && !/[.!?:;,]$/.test(lines[0]);

export const parseSummaryText = (text: string): SummaryBlock[] => {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const blocks: SummaryBlock[] = [];
  let paragraph: string[] = [];
  let items: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
    paragraph = [];
  };
  const flushList = (): void => {
    if (items.length === 0) return;
    blocks.push({ type: 'list', items });
    items = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line.length === 0) {
      flushParagraph();
      flushList();
      continue;
    }
    if (BULLET.test(line)) {
      flushParagraph();
      items.push(line.replace(BULLET, ''));
      continue;
    }
    if (items.length > 0 && /^\s/.test(rawLine)) {
      // An indented continuation of the previous bullet.
      items[items.length - 1] = `${items[items.length - 1]} ${line}`;
      continue;
    }
    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();

  // Promote the paragraphs that are really headings: the first block, and any
  // short line that introduces a list.
  return blocks.map((block, index) => {
    if (block.type !== 'paragraph') return block;
    const lines = [block.text];
    const opensText = index === 0;
    const opensList = blocks[index + 1]?.type === 'list';
    return (opensText || opensList) && looksLikeHeading(lines) ? { type: 'heading', text: block.text } : block;
  });
};
